import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { scrollToId } from "./SmoothScroll";

const RotatingBadge = () => (
  <div className="absolute -left-10 bottom-16 w-32 h-32 sm:w-36 sm:h-36 z-20 hidden sm:block" data-testid="hero-badge">
    <svg viewBox="0 0 120 120" className="w-full h-full spin-slow">
      <defs><path id="c" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <circle cx="60" cy="60" r="58" fill="#281715" />
      <text fill="#FAF7F2" fontSize="10.5" letterSpacing="3.4" fontFamily="JetBrains Mono">
        <textPath href="#c">AFFORDABLE LUXURY • EVERYDAY CONFIDENCE •</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 grid place-items-center font-display italic text-3xl text-[#EBB1B6]">L</span>
  </div>
);

function HeroVisual() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const pillY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative mx-auto w-full max-w-[460px] lg:max-w-none" style={{ perspective: 1200 }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease: [0.76, 0, 0.24, 1] }}
        className="relative aspect-[3/4] arch overflow-hidden bg-[#F4EFE6] shadow-[0_40px_80px_-30px_rgba(40,23,21,0.45)]">
        <motion.img src="/images/hero.jpg" alt="Model wearing LUMI high-shine nude brown gloss" style={{ y: imgY, scale: 1.18 }}
          className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(255,240,230,0.35),transparent_55%)]" />
      </motion.div>
      <motion.div style={{ y: pillY }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute -right-2 sm:-right-8 top-[18%] z-20 rounded-2xl bg-[#FAF7F2]/85 backdrop-blur-xl border border-[#E6D7CC] p-4 w-52 shadow-xl" data-testid="hero-shade-pill">
        <p className="overline text-[#6E504A]">Wearing</p>
        <p className="font-display text-xl mt-1">Mocha Glaze</p>
        <div className="flex gap-1.5 mt-3">
          {["#5A3827", "#C99A7E", "#E3A3A9", "#7E3446"].map((c) => (
            <span key={c} className="w-6 h-6 rounded-full ring-2 ring-[#FAF7F2]" style={{ background: c }} />
          ))}
        </div>
      </motion.div>
      <RotatingBadge />
    </div>
  );
}

export function Hero() {
  return (
    <section className="relative grain overflow-hidden pt-[104px] lg:pt-[120px] pb-16 lg:pb-24" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[#EBB1B6]/35 blur-3xl" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline text-[#6E504A] flex items-center gap-3">
            <span className="w-10 h-px bg-[#6E504A]" /> Collection 01 — The Lip Edit
          </motion.p>
          <h1 className="font-display text-[#281715] mt-6 leading-[0.92] tracking-[-0.02em] text-[15vw] sm:text-[12vw] lg:text-[8.6vw] xl:text-[132px]" data-testid="hero-title">
            <MaskLine delay={0.25}>Glazed</MaskLine>
            <MaskLine delay={0.4}><span className="italic text-[#D88B94]">in your</span></MaskLine>
            <MaskLine delay={0.55}>own light.</MaskLine>
          </h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-14">
            <p className="max-w-sm text-base text-[#6E504A] leading-relaxed">
              High-shine glosses and melting lip oils in nude, brown and pink shades made to flatter every skin tone. Affordable luxury for everyday confidence.
            </p>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => scrollToId("collection")}
              data-testid="hero-shop-btn"
              className="group shrink-0 flex items-center gap-3 rounded-full bg-[#281715] text-[#FAF7F2] pl-7 pr-2 h-14 text-sm tracking-wide">
              Shop the collection
              <span className="w-10 h-10 rounded-full bg-[#EBB1B6] text-[#281715] grid place-items-center transition-transform duration-300 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="w-4 h-4" />
              </span>
            </motion.button>
          </motion.div>
        </div>
        <div className="lg:col-span-5"><HeroVisual /></div>
      </div>
    </section>
  );
}
